import axios from 'axios';

// Create axios instance
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expired or invalid (backend returns 403 for expired tokens)
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Clear the auth token from axios
      delete api.defaults.headers.common['Authorization'];
      
      // Trigger a logout event that the AuthContext can listen to
      window.dispatchEvent(new CustomEvent('auth-logout'));
      
      // Redirect to login page after a short delay to allow the event to propagate
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials, userType) => {
    const endpoint = userType === 'admin' ? '/auth/admin/login' : '/auth/company/login';
    return api.post(endpoint, credentials);
  },
  
  setAuthToken: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }
};

// Admin API
export const adminAPI = {
  // Companies
  getCompanies: () => api.get('/admin/companies'),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  
  // Cameras
  getCameras: () => api.get('/admin/cameras'),
  createCamera: async (data) => {
    // Create camera in database (MediaMTX is configured statically in mediamtx.yml)
    const cameraResponse = await api.post('/admin/cameras', data);
    console.log('Camera created - MediaMTX configuration is hardcoded in mediamtx.yml');
    return cameraResponse;
  },
  updateCamera: async (id, data) => {
    // First update camera in database
    const cameraResponse = await api.put(`/admin/cameras/${id}`, data);
    
    // MediaMTX configuration is hardcoded in mediamtx.yml
    console.log('Camera updated - MediaMTX configuration is hardcoded in mediamtx.yml');
    
    return cameraResponse;
  },
  assignCamera: (cameraId, companyId) => api.post(`/admin/cameras/${cameraId}/assign/${companyId}`),
  unassignCamera: (cameraId) => api.post(`/admin/cameras/${cameraId}/unassign`),
  deleteCamera: async (id) => {
    // First delete camera from database
    const cameraResponse = await api.delete(`/admin/cameras/${id}`);
    
    // MediaMTX configuration is hardcoded in mediamtx.yml
    console.log('Camera deleted - MediaMTX configuration is hardcoded in mediamtx.yml');
    
    return cameraResponse;
  },
  
  // Stats
  getStats: () => api.get('/admin/stats')
};

// Company API
export const companyAPI = {
  // Profile
  getProfile: () => api.get('/company/profile'),
  
  // Photos
  getPhotos: (cameraId, params) => 
    api.get(`/company/cameras/${cameraId}/photos`, { params }),
  
  // Cameras
  getCameras: () => api.get('/company/cameras'),
  
  // Timelapse
  getTimelapseJobs: () => api.get('/company/timelapse-jobs'),
  createTimelapse: (data) => api.post('/timelapse/create', data),
  getTimelapseJob: (id) => api.get(`/timelapse/jobs/${id}`),
  downloadTimelapse: (id) => api.get(`/timelapse/jobs/${id}/download`, { 
    responseType: 'blob' 
  }),
  deleteTimelapseJob: (id) => api.delete(`/timelapse/jobs/${id}`)
};


// Camera API
export const cameraAPI = {
  getCamera: (id) => api.get(`/cameras/${id}`),
  getCameraStream: (id) => api.get(`/cameras/${id}/stream`),
  updateCameraStatus: (id, status) => api.patch(`/cameras/${id}/status`, { is_active: status })
};

// Photos API
export const photoAPI = {
  getPhoto: (id) => api.get(`/photos/${id}`),
  getPhotoFile: (id) => api.get(`/photos/${id}/file`, { responseType: 'blob' }),
  deletePhoto: (id) => api.delete(`/photos/${id}`),
  uploadPhoto: (formData) => api.post('/photos/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
};

export default api;
